import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { LABELS, MarketingStats } from "@/components/MarketingStats";

const THEMES = [
  { v: "navy", l: "Navy", c: ["#0B2545", "#F59E0B"] },
  { v: "sunset", l: "Sunset", c: ["#7C2D12", "#FDE047"] },
  { v: "emerald", l: "Emerald", c: ["#064E3B", "#FBBF24"] },
  { v: "light", l: "Clean white", c: ["#F8FAFC", "#1D4ED8"] },
];

const SIZES = [
  { v: "square", l: "Square 1080x1080", use: "Instagram & Facebook posts" },
  { v: "story", l: "Story 1080x1920", use: "Instagram, Facebook & TikTok stories" },
  { v: "landscape", l: "Wide 1200x628", use: "X, LinkedIn & Facebook links" },
  { v: "letter", l: "Print 8.5x11", use: "Printed flyers & posters" },
];

const captions = (link) => [
  ["Instagram", `Need SR22 or auto insurance? Get your instant price online, now up to 20% lower. Personal, commercial auto & general liability in all 50 states + DC. Pay by card and print your policy in minutes. Get your price: ${link("instagram")}\n#SR22 #AutoInsurance #CarInsurance #CommercialAuto #SmallBusiness`],
  ["Facebook", `Need SR22 or auto insurance? Get your instant price online, now up to 20% lower. Personal, commercial auto & general liability in all 50 states + DC. Pay by card and print your policy in minutes. Get your price: ${link("facebook")}`],
  ["X (Twitter)", `SR22, auto, commercial auto & general liability insurance, now up to 20% lower. Instant price, buy online, print your policy today. ${link("x")} #SR22 #AutoInsurance`],
  ["LinkedIn", `Small business owners: commercial auto and general liability coverage you can price, buy and download online in minutes, now up to 20% lower. Certificates of insurance available instantly. ${link("linkedin")}`],
  ["TikTok", `Got an SR22 requirement? Get your price in 2 minutes and print your policy today. Up to 20% lower right now. Link: ${link("tiktok")} #SR22 #CarInsurance`],
];

const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Copied"), () => toast.error("Copy failed"));

export const MarketingPanel = () => {
  const [site, setSite] = useState(window.location.origin);
  const [source, setSource] = useState("instagram");
  const [theme, setTheme] = useState("navy");
  const [size, setSize] = useState("square");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const base = site.replace(/\/+$/, "");
  const link = (s) => `${base}/api/go/${s}`;
  const url = link(source);
  const valid = /^https?:\/\/[^\s/.]+\.[^\s/]{2,}$/.test(base) && url.length <= 200;
  const load = async (fmt = "png") => {
    const { data } = await api.get("/admin/marketing/flyer", { params: { size, url, fmt, theme }, responseType: "blob" });
    return data;
  };
  useEffect(() => {
    let alive = true;
    if (!valid) {
      setPreview(null);
      return undefined;
    }
    const t = setTimeout(() => {
      setBusy(true);
      load().then((b) => alive && setPreview(URL.createObjectURL(b))).catch(() => alive && setPreview(null)).finally(() => alive && setBusy(false));
    }, 400);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size, url, theme]);
  const download = async (fmt) => {
    if (!valid) return toast.error("Enter a valid web address starting with https://");
    try {
      const b = await load(fmt);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(b);
      a.download = `seutrade-flyer-${source}-${theme}-${size}.${fmt}`;
      a.click();
    } catch (e) {
      toast.error("Could not create the flyer. Check the QR link and try again.");
    }
  };
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1.1fr]" data-testid="marketing-panel">
      <div className="min-w-0 space-y-5">
        <div className="space-y-1.5">
          <Label htmlFor="mk-url">Your website address</Label>
          <Input id="mk-url" value={site} onChange={(e) => setSite(e.target.value.trim())} data-testid="marketing-url-input" />
          {!valid && <p className="text-xs text-red-600" data-testid="marketing-url-error">Enter your site address, like https://yoursite.com</p>}
          <p className="text-xs text-slate-500">Open admin on your live site so this is your live address. Scanning opens the instant quote page and is counted below.</p>
        </div>
        <div className="space-y-1.5">
          <Label>Where will you post it?</Label>
          <div className="flex flex-wrap gap-2">
            {Object.entries(LABELS).map(([k, l]) => (
              <button key={k} onClick={() => setSource(k)} data-testid={`marketing-source-${k}`}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${source === k ? "border-blue-600 bg-blue-50 text-blue-800" : "border-slate-200 hover:border-slate-400"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Style</Label>
          <div className="flex flex-wrap gap-2">
            {THEMES.map((t) => (
              <button key={t.v} onClick={() => setTheme(t.v)} data-testid={`marketing-theme-${t.v}`}
                className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs transition-colors ${theme === t.v ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-400"}`}>
                <span className="flex h-4 w-6 overflow-hidden rounded border border-slate-300"><span className="w-1/2" style={{ background: t.c[0] }} /><span className="w-1/2" style={{ background: t.c[1] }} /></span>{t.l}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {SIZES.map((s) => (
            <button key={s.v} onClick={() => setSize(s.v)} data-testid={`marketing-size-${s.v}`}
              className={`rounded-lg border p-3 text-left text-sm transition-colors ${size === s.v ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-400"}`}>
              <p className="font-semibold">{s.l}</p><p className="text-xs text-slate-500">{s.use}</p>
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button onClick={() => download("png")} className="bg-blue-700 text-white hover:bg-blue-800" data-testid="marketing-download-png-btn"><Download className="mr-2 h-4 w-4" /> Download PNG</Button>
          <Button variant="outline" onClick={() => download("pdf")} data-testid="marketing-download-pdf-btn"><Download className="mr-2 h-4 w-4" /> Download PDF</Button>
        </div>
        <div className="space-y-3">
          <p className="text-sm font-semibold">Ready-to-post captions</p>
          {captions(link).map(([k, t], i) => (
            <div key={k} className="rounded-lg border border-slate-200 bg-white p-3 text-sm" data-testid={`marketing-caption-${i}`}>
              <div className="mb-1 flex items-center justify-between"><span className="font-semibold">{k}</span>
                <button onClick={() => copy(t)} className="flex items-center gap-1 text-xs text-blue-700 hover:underline" data-testid={`marketing-copy-${i}`}><Copy className="h-3.5 w-3.5" /> Copy</button></div>
              <p className="whitespace-pre-wrap break-words text-slate-600">{t}</p>
            </div>
          ))}
        </div>
        <MarketingStats />
      </div>
      <div className="flex min-w-0 items-start justify-center rounded-xl border border-slate-200 bg-slate-100 p-4">
        {!valid ? <p className="mt-10 text-sm text-slate-500" data-testid="marketing-preview-empty">Preview appears once the QR link is a valid web address.</p> : busy && !preview ? <Loader2 className="mt-10 h-6 w-6 animate-spin text-slate-400" /> : preview && <img src={preview} alt="Flyer preview" className="max-h-[720px] w-auto rounded shadow-lg" data-testid="marketing-preview" />}
      </div>
    </div>
  );
};
